import { createContext, useContext } from "react";
import type { Id } from "@convex/_generated/dataModel";
import type { UploaderStatus } from "./uploader";
import type { FileTiming } from "./fileTime";

/**
 * There is one microphone, so there is one recorder. It lives above the router
 * in `App.tsx` - navigating away from the calendar must not stop a lecture.
 */
export type RecorderStatus =
  | "idle"
  | "requesting"
  | "recording"
  | "uploading"
  | "finalizing"
  | "done"
  | "error";

/**
 * Where the audio comes from. `mic` is the default input device; `system` is
 * whatever the machine is playing, captured through a screen/tab share.
 */
export type AudioSource = "mic" | "system";

/** What the current run is: a live capture, or a file dropped into the app. */
export type RecorderMode = "capture" | "upload";

export interface UploadState {
  fileName: string;
  totalBytes: number;
  /**
   * `extracting`: a dropped video is being turned into an MP3 in the browser,
   * and `progress` is that conversion. `joining`: several files are being
   * joined into one. `uploading`: the audio is on its way.
   */
  phase: "extracting" | "joining" | "uploading";
  /** 0..1 of the current phase - for uploads, what the server has durably written. */
  progress: number;
  /** Set while several files go up one after another: 0-based, of `count`. */
  part: { index: number; count: number } | null;
}

export interface BatchFile {
  file: File;
  timing: FileTiming;
  /** Best-known start, for ordering and display - see `startOf`. */
  startMs: number;
  /** False when `startMs` is only the file's modified time. */
  fromFile: boolean;
}

/**
 * Several files dropped or picked at once. They are put in recording order
 * and held here until the user says whether they are parts of one recording.
 */
export interface PendingBatch {
  /** In recording order once `probing` is over. */
  files: BatchFile[];
  lessonId: Id<"lessons"> | null;
  /** True while the files' own timestamps are being read. */
  probing: boolean;
}

export type BatchChoice = "join" | "separate";

export interface RecorderApi {
  status: RecorderStatus;
  /** The Convex row, once `start` has created it. */
  recordingId: Id<"recordings"> | null;
  lessonId: Id<"lessons"> | null;
  /** Chunks written to IndexedDB and not yet acked by ingest. */
  pending: number;
  connection: UploaderStatus;
  /** Wall-clock ms since capture started. */
  elapsedMs: number;
  error: string | null;
  /** True when a gap was reported at finalize - the audio is incomplete. */
  partial: boolean;
  source: AudioSource;
  setSource: (source: AudioSource) => void;
  /** False where the browser cannot share audio (every mobile browser today). */
  systemAudioSupported: boolean;
  mode: RecorderMode;
  /** The file being (or just) uploaded; null for a capture. */
  upload: UploadState | null;
  /** Files waiting on the join-or-separate question; null otherwise. */
  batch: PendingBatch | null;
  start: (lessonId?: Id<"lessons"> | null) => Promise<void>;
  /**
   * Sends audio files through the same pipeline as a capture. With a lesson
   * they are pinned there; without one each is matched by its own time. More
   * than one file opens `batch` and waits for `resolveBatch`.
   */
  uploadFiles: (files: File[], lessonId?: Id<"lessons"> | null) => Promise<void>;
  /** Answers the batch question; null drops the files. */
  resolveBatch: (choice: BatchChoice | null) => Promise<void>;
  stop: () => Promise<void>;
  /** Throws the capture away: local chunks deleted, Convex row marked failed. */
  cancel: () => Promise<void>;
  /** Clears a finished or failed run so the controls go back to idle. */
  reset: () => void;
}

export const RecorderContext = createContext<RecorderApi | null>(null);

export function useRecorder(): RecorderApi {
  const value = useContext(RecorderContext);
  if (value === null) {
    throw new Error("useRecorder must be used inside <RecorderProvider>");
  }
  return value;
}

/** Is this recorder busy with audio that would be lost by navigating away? */
export function isBusy(status: RecorderStatus): boolean {
  return status === "recording" || status === "uploading" || status === "finalizing";
}

export const INGEST_WS_URL = import.meta.env.VITE_INGEST_WS_URL ?? "";
